import { PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  Pagination,
  Row,
  Tag,
  Typography,
  message,
} from 'antd'
import { debounce } from 'lodash'
import { runInAction } from 'mobx'
import moment from 'moment'
import { inject, observer } from 'mobx-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import AccessControlAction from '../../../components/AccessControlAction'
import { TableBottomPaginationBlock } from '../../../components/Common/Table'
import TableComponent from '../../../components/Common/TableComponent'
import ContentBlockWrapper from '../../../components/ContentBlockWrapper'
import PageTitle from '../../../components/PageTitle'
import { systemName } from '../../../config'
import { ACL_ACTION_TYPE, ASSIGNEE_TYPE } from '../../../constants'
import DashboardLayout from '../../../layouts/DashboardLayout'
import { ExportExcel } from './ExportExcel/ExportExcel'
import FeedbackBoardCreate from './FeedbackBoardCreate'

const { RangePicker } = DatePicker
const { Title } = Typography

const FeedbackBoardPage = props => {
  const {
    selectPeopleStore,
    documentAccessibilityStore,
    authenticationStore,
    loadingAnimationStore,
    FeedbackProcedureStore,
    userGroupStore,
    aclStore,
    history,
  } = props
  const { currentUser } = authenticationStore
  const { departmentWithUserListV2 } = selectPeopleStore
  const { allDataGroupList } = userGroupStore
  const {
    isVisibleSelectUserDepartmentPopup,
    setIsVisibleSelectUserDepartmentPopup,
    handleOpenSelectUserDepartmentPopup,
    handleCancelSelectUserDepartment,
    selectUserData,
    selectDepartmentData,
    selectGroupData,
  } = selectPeopleStore
  const [form] = Form.useForm()
  const [openFeedbackHDQT, setOpenFeedbackHDQT] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [dateRange, setDateRange] = useState(null)
  const [deptDataLoaded, setDeptDataLoaded] = useState(false)
  const [groupDataLoaded, setGroupDataLoaded] = useState(false)
  const {
    totalCount,
    feedbackData,
    commentData,
    page,
    size,
    setQueryParamsSignDocument,
    queryParamsSignDocument,
  } = FeedbackProcedureStore
  const searchTimeoutRef = useRef(null)

  const fetchFeedback = async () => {
    loadingAnimationStore.setTableLoading(true)
    try {
      setQueryParamsSignDocument({
        title: searchText,
        startDate: dateRange ? dateRange[0].format('DD-MM-YYYY') : null,
        endDate: dateRange ? dateRange[1].format('DD-MM-YYYY') : null,
        page: page,
        size: size,
      })

      await FeedbackProcedureStore.getFeedbackProcedureList(
        queryParamsSignDocument
      )
    } finally {
      loadingAnimationStore.setTableLoading(false)
    }
  }

  const result = feedbackData.map(item => ({
    ...item,
    feedbackComments: item.feedbackComments.map(comment => ({
      ...comment,
      feedbackCommentDetails: Object.values(
        comment.feedbackCommentDetails
          .filter(detail => detail.voteType !== null)
          .reduce((acc, detail) => {
            const { feedbackUsername, createdAt } = detail
            if (
              !acc[feedbackUsername] ||
              new Date(acc[feedbackUsername].createdAt) < new Date(createdAt)
            ) {
              acc[feedbackUsername] = detail
            }
            return acc
          }, {})
      ),
    })),
  }))

  const findUserAliases = useCallback(
    assigneeCode => {
      if (!assigneeCode) return []
      let userName = assigneeCode
      let userCode
      departmentWithUserListV2?.forEach(dept => {
        const matched = dept.users?.find(
          user =>
            user.user_name === assigneeCode ||
            user.user_code === assigneeCode ||
            user.username === assigneeCode
        )
        if (matched) {
          userName = matched.user_name || matched.username || assigneeCode
          userCode = matched.user_code
        }
      })
      return [userName, userCode, assigneeCode].filter(Boolean)
    },
    [departmentWithUserListV2]
  )

  const collectPicUsernames = useCallback(
    assignees => {
      const canonicalSet = new Set()
      const knownIds = new Map()

      const addPerson = (...ids) => {
        const validIds = [
          ...new Set(
            ids.filter(Boolean).map(id => String(id).trim().toLowerCase())
          ),
        ]
        if (!validIds.length) return

        const existingCanonical = validIds
          .map(id => knownIds.get(id))
          .find(Boolean)
        const canonical = existingCanonical || validIds[0]

        if (!existingCanonical) {
          canonicalSet.add(canonical)
        }
        validIds.forEach(id => knownIds.set(id, canonical))
      }

      const uniquePicAssignees = []
      const seenAssigneeCodes = new Set()
      assignees?.forEach(assignee => {
        const code = assignee.assignee_code?.toString()
        if (!code || seenAssigneeCodes.has(code)) return
        seenAssigneeCodes.add(code)
        uniquePicAssignees.push(assignee)
      })

      uniquePicAssignees.forEach(assignee => {
        if (assignee.assignee_type === ASSIGNEE_TYPE.USER) {
          addPerson(...findUserAliases(assignee.assignee_code))
          return
        }

        if (assignee.assignee_type === ASSIGNEE_TYPE.DEPARTMENT) {
          const department = departmentWithUserListV2?.find(
            dept => dept.code === assignee.assignee_code
          )
          department?.users?.forEach(user => {
            addPerson(user.user_name, user.username, user.user_code)
          })
          return
        }

        if (assignee.assignee_type === ASSIGNEE_TYPE.GROUP) {
          const matchedGroup = allDataGroupList?.find(
            group => group.id?.toString() === assignee.assignee_code?.toString()
          )
          matchedGroup?.groupUsers?.forEach(groupUser => {
            const user = groupUser?.user || groupUser
            // Bỏ user inactive trong nhóm (status === false)
            if ((user?.status ?? groupUser?.status) === false) return

            const primary =
              user?.user_name ||
              user?.username ||
              user?.user_code ||
              groupUser?.user_name
            addPerson(
              ...findUserAliases(primary),
              user?.user_name,
              user?.username,
              user?.user_code,
              groupUser?.user_name
            )
          })
        }
      })

      return canonicalSet
    },
    [departmentWithUserListV2, allDataGroupList, findUserAliases]
  )

  const getUserCandidates = useCallback(
    user => {
      if (!user) return new Set()
      const candidates = new Set()

      const addCandidate = val => {
        if (val !== undefined && val !== null && String(val).trim() !== '') {
          candidates.add(String(val).trim().toLowerCase())
        }
      }

      addCandidate(user.username)
      addCandidate(user.user_name)
      addCandidate(user.user_code)
      addCandidate(user.id)

      // Tra cứu thêm alias từ departmentWithUserListV2
      departmentWithUserListV2?.forEach(dept => {
        dept.users?.forEach(u => {
          const isMatch =
            (u.username &&
              candidates.has(String(u.username).trim().toLowerCase())) ||
            (u.user_name &&
              candidates.has(String(u.user_name).trim().toLowerCase())) ||
            (u.user_code &&
              candidates.has(String(u.user_code).trim().toLowerCase())) ||
            (u.id && candidates.has(String(u.id).trim().toLowerCase()))

          if (isMatch) {
            addCandidate(u.username)
            addCandidate(u.user_name)
            addCandidate(u.user_code)
            addCandidate(u.id)
          }
        })
      })

      // Tra cứu thêm alias từ allDataGroupList
      allDataGroupList?.forEach(group => {
        group.groupUsers?.forEach(groupUser => {
          const u = groupUser?.user || groupUser
          const isMatch =
            (u?.username &&
              candidates.has(String(u.username).trim().toLowerCase())) ||
            (u?.user_name &&
              candidates.has(String(u.user_name).trim().toLowerCase())) ||
            (u?.user_code &&
              candidates.has(String(u.user_code).trim().toLowerCase())) ||
            (u?.id && candidates.has(String(u.id).trim().toLowerCase())) ||
            (groupUser?.user_name &&
              candidates.has(String(groupUser.user_name).trim().toLowerCase()))

          if (isMatch) {
            addCandidate(u?.username)
            addCandidate(u?.user_name)
            addCandidate(u?.user_code)
            addCandidate(u?.id)
            addCandidate(groupUser?.user_name)
          }
        })
      })

      return candidates
    },
    [departmentWithUserListV2, allDataGroupList]
  )

  const getAllPicIdentifiers = useCallback(
    assignees => {
      const picIdentifiers = new Set()

      const addPicId = val => {
        if (val !== undefined && val !== null && String(val).trim() !== '') {
          picIdentifiers.add(String(val).trim().toLowerCase())
        }
      }

      const picAssignees =
        assignees?.filter(
          a => a.permission === 'PIC' || a.permission === ASSIGNEE_TYPE.HANDLER
        ) || []

      picAssignees.forEach(assignee => {
        const code = assignee.assignee_code?.toString()?.trim()
        if (!code) return

        const type = assignee.assignee_type

        // 1. Assignee là USER
        if (type === ASSIGNEE_TYPE.USER || type === 'USER') {
          addPicId(code)
          departmentWithUserListV2?.forEach(dept => {
            const matched = dept.users?.find(
              u =>
                String(u.username).toLowerCase() === code.toLowerCase() ||
                String(u.user_name).toLowerCase() === code.toLowerCase() ||
                String(u.user_code).toLowerCase() === code.toLowerCase() ||
                String(u.id).toLowerCase() === code.toLowerCase()
            )
            if (matched) {
              addPicId(matched.username)
              addPicId(matched.user_name)
              addPicId(matched.user_code)
              addPicId(matched.id)
            }
          })
          allDataGroupList?.forEach(group => {
            group.groupUsers?.forEach(groupUser => {
              const u = groupUser?.user || groupUser
              const matched =
                String(u?.username).toLowerCase() === code.toLowerCase() ||
                String(u?.user_name).toLowerCase() === code.toLowerCase() ||
                String(u?.user_code).toLowerCase() === code.toLowerCase() ||
                String(groupUser?.user_name).toLowerCase() ===
                  code.toLowerCase()
              if (matched) {
                addPicId(u?.username)
                addPicId(u?.user_name)
                addPicId(u?.user_code)
                addPicId(groupUser?.user_name)
              }
            })
          })
        }

        // 2. Assignee là DEPARTMENT
        if (
          type === ASSIGNEE_TYPE.DEPARTMENT ||
          type === 'DEPARTMENT' ||
          type === 'DPT'
        ) {
          const department = departmentWithUserListV2?.find(
            dept =>
              String(dept.code).toLowerCase() === code.toLowerCase() ||
              String(dept.id).toLowerCase() === code.toLowerCase()
          )
          department?.users?.forEach(user => {
            addPicId(user.username)
            addPicId(user.user_name)
            addPicId(user.user_code)
            addPicId(user.id)
          })
        }

        // 3. Assignee là GROUP
        if (
          type === ASSIGNEE_TYPE.GROUP ||
          type === 'GROUP' ||
          type === 'GRP'
        ) {
          const matchedGroup = allDataGroupList?.find(
            group =>
              String(group.id).toLowerCase() === code.toLowerCase() ||
              String(group.code).toLowerCase() === code.toLowerCase()
          )
          matchedGroup?.groupUsers?.forEach(groupUser => {
            const user = groupUser?.user || groupUser
            if ((user?.status ?? groupUser?.status) === false) return
            addPicId(user?.username)
            addPicId(user?.user_name)
            addPicId(user?.user_code)
            addPicId(user?.id)
            addPicId(groupUser?.user_name)
          })
        }
      })

      return picIdentifiers
    },
    [departmentWithUserListV2, allDataGroupList]
  )

  const isUserPicOfItem = useCallback(
    (item, user) => {
      if (!item || !user) return false

      const userCandidates = getUserCandidates(user)
      if (userCandidates.size === 0) return false

      // 1. Kiểm tra trong danh sách PIC assignees
      const picIdentifiers = getAllPicIdentifiers(item.assignees)
      for (const id of userCandidates) {
        if (picIdentifiers.has(id)) {
          return true
        }
      }

      // 2. Kiểm tra nếu user đã từng biểu quyết trong phiếu này
      const hasVotedBefore = (item.feedbackComments || []).some(comment =>
        (comment.feedbackCommentDetails || []).some(d => {
          if (!d.feedbackUsername) return false
          const voter = String(d.feedbackUsername).toLowerCase().trim()
          return userCandidates.has(voter)
        })
      )
      if (hasVotedBefore) return true

      // 3. Nếu phiếu không có phân công PIC cụ thể nhưng user có quyền biểu quyết HĐQT
      const hasPicAssignee = (item.assignees || []).some(
        a => a.permission === 'PIC' || a.permission === ASSIGNEE_TYPE.HANDLER
      )
      const hasVotingAction = aclStore?.aclActionsByUser?.some(
        act => act.code === ACL_ACTION_TYPE.voting__HDQT && act.status
      )
      if (!hasPicAssignee && hasVotingAction) {
        return true
      }

      return false
    },
    [getUserCandidates, getAllPicIdentifiers, aclStore?.aclActionsByUser]
  )

  const newFeedbackData = useMemo(() => {
    if (!deptDataLoaded || !groupDataLoaded || !feedbackData) {
      return []
    }

    return feedbackData.map(item => {
      const picAssignees =
        item.assignees?.filter(a => a.permission === 'PIC') || []
      const eligibleVoterCount = collectPicUsernames(picAssignees).size

      const calcPercent = count =>
        eligibleVoterCount > 0
          ? Number(((count / eligibleVoterCount) * 100).toFixed(2))
          : 0

      const feedbackCommentsWithPercent = (item.feedbackComments || []).map(
        comment => {
          const latestVotes = (comment.feedbackCommentDetails || [])
            .filter(detail => detail.voteType)
            .reduce((acc, detail) => {
              const { feedbackUsername, createdAt } = detail
              if (
                !acc[feedbackUsername] ||
                new Date(acc[feedbackUsername].createdAt) < new Date(createdAt)
              ) {
                acc[feedbackUsername] = detail
              }
              return acc
            }, {})

          const voteCounts = Object.values(latestVotes).reduce(
            (counts, detail) => {
              if (detail.voteType in counts) {
                counts[detail.voteType] += 1
              }
              return counts
            },
            { AGREE: 0, DISAGREE: 0, OTHER: 0 }
          )

          return {
            ...comment,
            agreePercent: calcPercent(voteCounts.AGREE),
            disagreePercent: calcPercent(voteCounts.DISAGREE),
            otherPercent: calcPercent(voteCounts.OTHER),
          }
        }
      )

      // Trung bình % các nội dung ý kiến (khớp logic từng mục trên detail)
      const commentsWithContent = feedbackCommentsWithPercent.filter(
        c => c.content
      )
      const avg = key =>
        commentsWithContent.length > 0
          ? Number(
              (
                commentsWithContent.reduce((sum, c) => sum + (c[key] || 0), 0) /
                commentsWithContent.length
              ).toFixed(2)
            )
          : 0

      return {
        ...item,
        feedbackComments: feedbackCommentsWithPercent,
        totalPeopleCountv2: eligibleVoterCount,
        agreePercentage: avg('agreePercent'),
        disagreePercentage: avg('disagreePercent'),
        otherPercentage: avg('otherPercent'),
      }
    })
  }, [
    feedbackData,
    departmentWithUserListV2,
    allDataGroupList,
    deptDataLoaded,
    groupDataLoaded,
    collectPicUsernames,
  ])

  const handleOpenCreateFeedback = () => {
    setOpenFeedbackHDQT(true)
  }

  const handleCloseCreateFeedback = () => {
    setOpenFeedbackHDQT(false)
  }

  const isCurrentUserHandler = useMemo(() => {
    if (!currentUser) return false

    // Kiểm tra quyền biểu quyết HĐQT từ ACL
    const hasVotingAction = aclStore?.aclActionsByUser?.some(
      act => act.code === ACL_ACTION_TYPE.voting__HDQT && act.status
    )
    if (hasVotingAction) return true

    // Kiểm tra user có được gán làm Người xử lý (PIC) trong danh sách phiếu hiện tại hay không
    const listToCheck =
      (newFeedbackData?.length > 0 ? newFeedbackData : feedbackData) || []
    if (listToCheck.length > 0) {
      const isPicInList = listToCheck.some(item =>
        isUserPicOfItem(item, currentUser)
      )
      if (isPicInList) return true
    }

    return false
  }, [
    currentUser,
    newFeedbackData,
    feedbackData,
    isUserPicOfItem,
    aclStore?.aclActionsByUser,
  ])

  const getWarningStatus = useCallback(
    record => {
      const isPic = isUserPicOfItem(record, currentUser)
      if (!isPic) {
        return null // Không phải người xử lý của phiếu này -> hiển thị "-"
      }

      // Nội dung biểu quyết là các comment có content
      const relevantComments = (record.feedbackComments || []).filter(
        c => c.content && c.content.trim() !== ''
      )
      const commentsToVote =
        relevantComments.length > 0
          ? relevantComments
          : record.feedbackComments || []

      const userCandidates = getUserCandidates(currentUser)

      // Hoàn thành: Đã biểu quyết đầy đủ tất cả nội dung trong phiếu
      const hasCompletedAll =
        commentsToVote.length > 0 &&
        commentsToVote.every(comment => {
          const latestVote = (comment.feedbackCommentDetails || [])
            .filter(detail => {
              const voter = String(detail.feedbackUsername || '')
                .toLowerCase()
                .trim()
              return (
                userCandidates.has(voter) &&
                !detail.isDeleted &&
                Boolean(detail.voteType)
              )
            })
            .reduce((latest, detail) => {
              if (
                !latest ||
                new Date(detail.createdAt || 0) >
                  new Date(latest.createdAt || 0)
              ) {
                return detail
              }
              return latest
            }, null)

          return Boolean(latestVote)
        })

      if (hasCompletedAll) {
        return {
          status: 'COMPLETED',
          text: 'Hoàn thành',
          color: 'green',
        }
      }

      // Kiểm tra quá hạn: Chưa biểu quyết đủ tất cả nội dung và ngày hiện tại đã quá ngày kết thúc
      const isOverdue = record?.endDate
        ? moment().isAfter(moment(record.endDate).endOf('day'))
        : false

      if (isOverdue) {
        return {
          status: 'OVERDUE',
          text: 'Quá hạn',
          color: 'red',
        }
      }

      // Chưa đến hạn: Chưa biểu quyết đủ tất cả nội dung và chưa đến ngày kết thúc
      return {
        status: 'PENDING',
        text: 'Chưa đến hạn',
        color: 'orange',
      }
    },
    [isUserPicOfItem, currentUser, getUserCandidates]
  )

  const debouncedFetchFeedback = useMemo(() => debounce(fetchFeedback, 300), [
    searchText,
    dateRange,
  ])

  useEffect(() => {
    debouncedFetchFeedback()
  }, [searchText, dateRange])

  useEffect(() => {
    const loadDepartmentData = async () => {
      if (
        !selectPeopleStore.departmentWithUserListV2 ||
        selectPeopleStore.departmentWithUserListV2.length === 0
      ) {
        try {
          loadingAnimationStore.showSpinner(true)
          await selectPeopleStore.getDepartmentWithUsersV2()
        } catch (error) {
          message.error('Không tải được danh sách phòng ban')
          console.error(error)
        } finally {
          loadingAnimationStore.showSpinner(false)
        }
      }
      setDeptDataLoaded(true)
    }

    loadDepartmentData()
  }, [selectPeopleStore])

  useEffect(() => {
    const loadGroupData = async () => {
      if (!allDataGroupList || allDataGroupList.length === 0) {
        try {
          await userGroupStore.getAllGroupList()
        } catch (error) {
          console.error('Không tải được danh sách nhóm người dùng', error)
        }
      }
      setGroupDataLoaded(true)
    }

    loadGroupData()
  }, [userGroupStore])

  const handleSearchChange = e => {
    setSearchText(e.target.value)
  }

  const handleDateRangeChange = dates => {
    setDateRange(dates)
  }

  const exportFeedbackProcedure = async () => {
    try {
      runInAction(() => {
        FeedbackProcedureStore.page = 0
        FeedbackProcedureStore.size = 999999
        FeedbackProcedureStore.queryParamsSignDocument.page = 0
        FeedbackProcedureStore.queryParamsSignDocument.size = 999999
      })
      FeedbackProcedureStore.getFeedbackProcedureList()
      await ExportExcel(FeedbackProcedureStore.feedbackData)
    } catch (error) {
      console.log(error)
    } finally {
      runInAction(() => {
        FeedbackProcedureStore.page = page
        FeedbackProcedureStore.size = size
        FeedbackProcedureStore.queryParamsSignDocument.page = page
        FeedbackProcedureStore.queryParamsSignDocument.size = page
      })
    }
  }

  const onChangePage = async (page, pageSize) => {
    runInAction(() => {
      FeedbackProcedureStore.page = page - 1
      FeedbackProcedureStore.size = pageSize
      FeedbackProcedureStore.queryParamsSignDocument.page = page - 1
      FeedbackProcedureStore.queryParamsSignDocument.size = pageSize
    })

    loadingAnimationStore.setTableLoading(true)

    try {
      await FeedbackProcedureStore.getFeedbackProcedureList()
    } finally {
      loadingAnimationStore.setTableLoading(false)
    }
  }

  // Helper function để tính % cho từng comment
  const calculateCommentPercentages = (record, comment) => {
    // Ưu tiên % đã tính sẵn trong newFeedbackData (tránh lệch lần đầu load)
    if (
      comment?.agreePercent != null ||
      comment?.disagreePercent != null ||
      comment?.otherPercent != null
    ) {
      return {
        agreePercent: comment.agreePercent || 0,
        disagreePercent: comment.disagreePercent || 0,
        otherPercent: comment.otherPercent || 0,
      }
    }

    const picAssignees =
      record.assignees?.filter(a => a.permission === 'PIC') || []
    const totalPeopleCount = collectPicUsernames(picAssignees).size

    const latestVotes = (comment.feedbackCommentDetails || [])
      .filter(detail => detail.voteType)
      .reduce((acc, detail) => {
        const { feedbackUsername, createdAt } = detail
        if (
          !acc[feedbackUsername] ||
          new Date(acc[feedbackUsername].createdAt) < new Date(createdAt)
        ) {
          acc[feedbackUsername] = detail
        }
        return acc
      }, {})

    const voteCounts = Object.values(latestVotes).reduce(
      (counts, detail) => {
        if (detail.voteType in counts) {
          counts[detail.voteType] += 1
        }
        return counts
      },
      { AGREE: 0, DISAGREE: 0, OTHER: 0 }
    )

    return {
      agreePercent:
        totalPeopleCount > 0
          ? Number(((voteCounts.AGREE / totalPeopleCount) * 100).toFixed(2))
          : 0,
      disagreePercent:
        totalPeopleCount > 0
          ? Number(((voteCounts.DISAGREE / totalPeopleCount) * 100).toFixed(2))
          : 0,
      otherPercent:
        totalPeopleCount > 0
          ? Number(((voteCounts.OTHER / totalPeopleCount) * 100).toFixed(2))
          : 0,
    }
  }

  const tableColumns = useMemo(
    () => [
      {
        title: 'Số PLYK',
        width: 120,
        align: 'left',
        render: (text, record) => {
          const value = record.documentNumber
            ? record.documentNumber.includes('/')
              ? record.documentNumber
              : `${record.documentNumber}/PLYK-HĐQT`
            : '-'
          return <p style={{ marginBottom: '0px' }}>{value}</p>
        },
      },
      {
        title: 'Nội dung ý kiến',
        width: 400,
        render: (text, record, index) => {
          // Lấy text thuần từ HTML
          const getPlainText = html => {
            const div = document.createElement('div')
            div.innerHTML = html
            return div.textContent || div.innerText || ''
          }

          return (
            <div>
              <div
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginBottom: '8px',
                  fontWeight: 600,
                }}
                dangerouslySetInnerHTML={{
                  __html: record.title,
                }}></div>

              {/* Hiển thị các nội dung ý kiến */}
              {record.feedbackComments?.map((comment, idx) => {
                if (!comment.content) return null

                const plainText = getPlainText(comment.content)
                const truncatedText =
                  plainText.length > 100
                    ? plainText.substring(0, 100) + '...'
                    : plainText

                return (
                  <div
                    key={idx}
                    style={{
                      marginBottom: '6px',
                      paddingLeft: '12px',
                    }}>
                    <div
                      title={plainText}
                      style={{
                        fontSize: '13px',
                      }}>
                      {'- ' + truncatedText}
                    </div>
                  </div>
                )
              })}
            </div>
          )
        },
      },
      {
        title: 'Ngày bắt đầu',
        width: 100,
        align: 'center',
        render: (text, record) => {
          if (!record?.startDate)
            return <p style={{ marginBottom: '0px' }}>-</p>

          const formattedDate = moment(record.startDate).isValid()
            ? moment(record.startDate).format('DD/MM/YYYY')
            : '-'

          return <p style={{ marginBottom: '0px' }}>{formattedDate}</p>
        },
      },
      {
        title: 'Ngày kết thúc',
        width: 100,
        align: 'center',
        render: (text, record) => {
          if (!record?.endDate) return <p style={{ marginBottom: '0px' }}>-</p>

          const formattedDate = moment(record.endDate).isValid()
            ? moment(record.endDate).format('DD/MM/YYYY')
            : '-'

          return <p style={{ marginBottom: '0px' }}>{formattedDate}</p>
        },
      },
      ...(isCurrentUserHandler
        ? [
            {
              title: 'Cảnh báo',
              width: 120,
              align: 'center',
              render: (text, record) => {
                const warning = getWarningStatus(record)
                if (!warning) {
                  return <p style={{ marginBottom: '0px' }}>-</p>
                }
                return (
                  <Tag
                    color={warning.color}
                    style={{
                      borderRadius: 10,
                      margin: 0,
                      fontWeight: 500,
                    }}>
                    {warning.text}
                  </Tag>
                )
              },
            },
          ]
        : []),
      {
        title: 'Kết quả',
        width: 100,
        render: (text, record) => {
          if (record.feedbackComments?.length >= 2) {
            return (
              <div>
                {record.feedbackComments.map((comment, idx) => {
                  if (!comment.content) return null
                  const {
                    agreePercent,
                    disagreePercent,
                    otherPercent,
                  } = calculateCommentPercentages(record, comment)

                  const result =
                    agreePercent > disagreePercent &&
                    agreePercent > otherPercent
                      ? 'Thông qua'
                      : 'Chưa thông qua'

                  return (
                    <p
                      key={idx}
                      style={{
                        marginBottom: '0px',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                      }}>
                      {result}
                    </p>
                  )
                })}
              </div>
            )
          }
          return (
            <p
              style={{
                marginBottom: '0px',
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center',
              }}>
              {record?.agreePercentage > record?.disagreePercentage &&
              record?.agreePercentage > record?.otherPercentage
                ? 'Thông qua'
                : 'Chưa thông qua'}
            </p>
          )
        },
      },
      {
        title: 'Tán thành',
        align: 'center',
        width: 100,
        render: (text, record) => {
          if (record.feedbackComments?.length >= 2) {
            return (
              <div>
                {record.feedbackComments.map((comment, idx) => {
                  if (!comment.content) return null
                  const { agreePercent } = calculateCommentPercentages(
                    record,
                    comment
                  )
                  return (
                    <p
                      key={idx}
                      style={{
                        marginBottom: '0px',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      {agreePercent.toFixed(2)}%
                    </p>
                  )
                })}
              </div>
            )
          }
          return (
            <p style={{ marginBottom: '0px' }}>
              {record?.agreePercentage?.toFixed(2)}%
            </p>
          )
        },
      },
      {
        title: 'Không tán thành',
        align: 'center',
        width: 120,
        render: (text, record) => {
          if (record.feedbackComments?.length >= 2) {
            return (
              <div>
                {record.feedbackComments.map((comment, idx) => {
                  if (!comment.content) return null
                  const { disagreePercent } = calculateCommentPercentages(
                    record,
                    comment
                  )
                  return (
                    <p
                      key={idx}
                      style={{
                        marginBottom: '0px',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      {disagreePercent.toFixed(2)}%
                    </p>
                  )
                })}
              </div>
            )
          }
          return (
            <p style={{ marginBottom: '0px' }}>
              {record?.disagreePercentage?.toFixed(2)}%
            </p>
          )
        },
      },
      {
        title: 'Không có ý kiến',
        align: 'center',
        width: 120,
        render: (text, record) => {
          if (record.feedbackComments?.length >= 2) {
            return (
              <div>
                {record.feedbackComments.map((comment, idx) => {
                  if (!comment.content) return null
                  const { otherPercent } = calculateCommentPercentages(
                    record,
                    comment
                  )
                  return (
                    <p
                      key={idx}
                      style={{
                        marginBottom: '0px',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      {otherPercent.toFixed(2)}%
                    </p>
                  )
                })}
              </div>
            )
          }
          return (
            <p style={{ marginBottom: '0px' }}>
              {record?.otherPercentage?.toFixed(2)}%
            </p>
          )
        },
      },
    ],
    [
      departmentWithUserListV2,
      allDataGroupList,
      newFeedbackData,
      isCurrentUserHandler,
      getWarningStatus,
    ]
  )

  return (
    <>
      <DashboardLayout>
        <Helmet>
          <title>Phiếu lấy ý kiến | {systemName} Portal</title>
        </Helmet>
        <PageTitle location={props.location}>
          <div>
            <AccessControlAction aclActionType={ACL_ACTION_TYPE.create__HDQT}>
              <Button
                onClick={handleOpenCreateFeedback}
                type={'primary'}
                icon={<PlusOutlined />}>
                Tạo mới
              </Button>
            </AccessControlAction>
          </div>
        </PageTitle>
        <ContentBlockWrapper>
          <Row gutter={24 || 24}>
            <Col span={6}>
              <Form.Item
                style={{ marginBottom: 0, paddingBottom: 0 }}
                name={'search'}
                rules={[{ required: false }]}>
                <Input
                  allowClear
                  placeholder={'Tìm kiếm theo nội dung'}
                  value={searchText}
                  onChange={handleSearchChange}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                style={{ marginBottom: 0, paddingBottom: 0 }}
                name={'dateRange'}
                rules={[{ required: false }]}>
                <RangePicker
                  format={'DD/MM/YYYY'}
                  style={{ width: '100%' }}
                  value={dateRange}
                  onChange={handleDateRangeChange}
                />
              </Form.Item>
            </Col>
          </Row>
          <TableComponent
            rowKey={record => record.code}
            onRow={record => {
              return {
                onClick: () => {
                  history.push(
                    `/feedback-hdqt/feedback-detail/view/${record.code}`
                  )
                },
              }
            }}
            dataSource={newFeedbackData}
            columns={tableColumns}
            pagination={false}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={'Không có dữ liệu'}
                />
              ),
            }}
            loading={{
              tip: 'Đang tải phiếu ý kiến...',
              spinning:
                loadingAnimationStore.tableLoading ||
                !deptDataLoaded ||
                !groupDataLoaded,
            }}
          />
          <TableBottomPaginationBlock>
            <Pagination
              current={page + 1}
              total={totalCount}
              pageSize={size}
              hideOnSinglePage={true}
              onChange={onChangePage}
            />
          </TableBottomPaginationBlock>
        </ContentBlockWrapper>
      </DashboardLayout>
      {openFeedbackHDQT && (
        <FeedbackBoardCreate
          isVisible={openFeedbackHDQT}
          setIsVisible={setOpenFeedbackHDQT}
          handleCancel={handleCloseCreateFeedback}
          fetchFeedback={fetchFeedback}
        />
      )}
    </>
  )
}

export default inject(
  'selectUserStore',
  'documentAccessibilityStore',
  'selectPeopleStore',
  'loadingAnimationStore',
  'authenticationStore',
  'FeedbackProcedureStore',
  'userGroupStore',
  'aclStore'
)(observer(FeedbackBoardPage))
